import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, ExecuteRunner } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_EVL_REG_003_POP_001 from "@menu/MTL/SN_MTL_EVL_REG_003/SN_MTL_EVL_REG_003_POP_001";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";
import Badge from "@mui/material/Badge";

interface Props {
  Menuprops: menuInfo;
}

/** 재고평가 방식(SYS_CONFIG.MTL0001) — '03' 선입선출일 때만 오류메시지를 쓴다. */
const MTL0001_FIFO = "03";
/** sys_headerctrl 의 cd_form. 헤더 컨트롤은 그리드 단위로 묶인다. */
const HEADER_FORM = "grid1";

const SN_MTL_EVL_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const { userInfo, config } = global(); // Login user 정보
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [MTL0001, setMTL0001] = useState("");
  /**
   * 상단 버튼 메타(sys_headerctrl). 재고평가처럼 배지가 없는 버튼은 여기서 그린다.
   * getInitFormData 가 이미 { controls, data } 로 가공해 주므로 그대로 넘긴다
   * (다시 감싸면 controls 가 객체가 되어 SnHeader 의 filter 에서 조용히 죽는다).
   */
  const [headerInfo, setHeaderInfo] = useState<any>({
    controls: [],
    data: {},
  });
  /** 오류메시지 건수 — 배지 숫자. 조회할 때마다 갱신한다. */
  const [msgCount, setMsgCount] = useState(0);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
        setHeaderInfo(
          rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} }
        );
      } catch (error) {}
    };
    fetchform();

    setMTL0001(config?.MTL0001?.dc_ctrl ?? "");
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
    onSave,
    onExecute,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      // 평가결과는 마감월(dt_to) 한 달에만 쌓인다. 조회쿼리(_01)도 dt_to 로 건다.
      searchDataToUse.dt_from = searchDataToUse.dt_from.slice(0, 6);
      searchDataToUse.dt_to = String(searchDataToUse.dt_to ?? "").slice(0, 6);

      const params = {
        id: "sn_mtl_evl_reg_003_01",
        params: searchDataToUse,
      };

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
      });

      await loadMsgCount(searchDataToUse);
    } catch (error: any) {}
  };
  //#endregion

  /**
   * 오류메시지 건수 조회(배지용).
   * 팝업과 같은 조건이지만 count 만 세는 별도 쿼리다 — 팝업 쿼리를 재사용하면
   * 배지 하나 때문에 전체 행을 가져오게 된다.
   * mtl_aminventory_fifo_msg.ym 은 YYYYMM 이라 기간을 6자리로 잘라 넘긴다.
   */
  const loadMsgCount = async (searchData: any) => {
    if (MTL0001 !== MTL0001_FIFO) return;
    try {
      const data: any = await fnGetDataSet({
        // _02 는 재고평가 중복체크(mtl_aminventory)로 이미 쓰이는 id 다. 겹치지 않게 _03.
        id: "sn_mtl_evl_reg_003_03",
        params: {
          ...searchData,
          dt_from: String(searchData?.dt_from ?? "").slice(0, 6),
          dt_to: String(searchData?.dt_to ?? "").slice(0, 6),
        },
      });
      setMsgCount(Number(data?.[0]?.[0]?.qt_msg ?? 0));
    } catch (error: any) {
      setMsgCount(0);
    }
  };

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid1").deleteAllRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      return;
    }

    const saved = await onSave();
    // 저장만 하면 화면이 삭제 전 상태로 남는다. 오류메시지 배지도 같이 다시 센다.
    if (saved) await onSearch(null);
    return saved;
  };
  //endregion

  //#region onSave
  const onSave = async () => {
    try {
      const aminventoryChanges = getGridCurrent("grid1").getChange();

      if (aminventoryChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      // 요청 데이터 구성
      const SaveData = {
        aminventory: aminventoryChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_EVL_REG_003_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    switch (id) {
      case "Est": // 재고평가
        {
          const searchInfo = searchRef.current.getFormdata();
          const params1 = {
            id: "sn_mtl_evl_reg_003_02",
            params: searchInfo,
          };

          const data1: any = await fnGetDataSet(params1);

          if (data1[0][0].cnt !== 0) {
            await showDialog(
              "alter",
              "해당 기간의 재고평가가 이미 완료되었습니다."
            );
            return;
          }

          const confirm = await showDialog(
            "confirm",
            "재고평가를 진행하시겠습니까?"
          );

          if (!confirm) {
            return; // 사용자가 아니오를 선택하면 함수 종료
          }

          const param2 = {
            cd_firm: searchInfo.cd_firm,
            cd_biz: searchInfo.cd_biz,
            // ym 은 마감월이다. ym_start 와 같이 dt_from 을 넣으면 시작월 한 달만 평가된다.
            // 중복체크(_02)는 ym between dt_from and dt_to 로 구간을 보므로,
            // 첫 달만 생성되면 다음 실행부터 "이미 완료" 로 막혀 나머지 달을 못 만든다.
            ym: searchInfo.dt_to.slice(0, 6),
            ym_start: searchInfo.dt_from.slice(0, 6),
            fg_eval: MTL0001,
            cd_dept: userInfo.cd_dept,
            cd_emp: userInfo.cd_emp,
            cd_user: userInfo.cd_user,
          };

          const ParamData = {
            executeEvl: param2,
          };

          // 저장 API 호출
          const ok = await fnSave("SN_MTL_EVL_REG_003_SAVE_02", ParamData);
          if (!ok) return false;

          onSearch(null);
        }
        break;
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Msg":
        const selectedFormData = searchRef.current.getFormdata();

        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };
        const component = (
          <SN_MTL_EVL_REG_003_POP_001 selectedData={selectedData} />
        );
        await showResizablePopupModal({
          title: "오류메시지",
          children: component,
          width: "1400px",
          height: "800px",
          okayBtn: false,
        });
        break;
      // sys_headerctrl 의 재고평가 버튼(cd_param = 'Est')
      case "Est":
        ExecuteRunner.run("Est");
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch(null);
    }, 0);
  };

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnGrid
          // 배지가 붙는 버튼만 직접 그린다(body 슬롯 = 메타 버튼 왼쪽).
          // SnButton 은 배지 prop 이 없고, 건수는 조회 결과라 메타에 담을 수도 없다.
          header={[
            <div style={{ paddingLeft: "80px" }}>
              <Badge
                badgeContent={msgCount}
                color="error"
                invisible={msgCount === 0}
              >
                <SnButton
                  text={"오류메시지"}
                  visible={MTL0001 === MTL0001_FIFO}
                  onClick={() => {
                    onSnButtonClick("Msg");
                  }}
                />
              </Badge>
            </div>,
          ]}
          // 재고평가는 sys_headerctrl 메타로 그린다(cd_param = 'Est').
          headerInfo={headerInfo}
          events={{ onBtnEtcClick: onSnButtonClick }}
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{ rowSelection: "multiple", title: "평가정보" }}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_EVL_REG_003;
